import type {
  ChecklistPointDto,
  ChecklistTemplateDto,
  ChecklistTemplateKind,
  DictationBody,
  DictationResultDto,
  DictationSuggestionDto,
  IntakeConfirmResultDto,
  IntakeContextDto,
  IntakeItemDto,
  IntakeSessionDto,
  IntakeSessionViewDto,
  RoomFieldDto,
  RoomTemplateDto,
  UpsertSessionBody,
} from '@reos/field-intake';
import { baseUrl } from './api.js';
import { DomainApiError } from './api-error.js';

/**
 * Krok UXV4c — nabor na miste nad `/v1/field-intake/*`. Raw `fetch` (vzor
 * `ai-credits-api.ts`, AKV1): routy jeste nejsou v generovanem klientu;
 * typy `import type` z kontextu = jeden zdroj tvaru (hlida
 * `test/browser-safe-imports.spec.ts`).
 */

export type { ChecklistPointDto, ChecklistTemplateDto, ChecklistTemplateKind, DictationResultDto, DictationSuggestionDto, IntakeConfirmResultDto, IntakeContextDto, IntakeItemDto, IntakeSessionDto, IntakeSessionViewDto, RoomFieldDto, RoomTemplateDto };

const PREFIX = '/v1/field-intake';

/** Sitova chyba (offline) — fronta ji odlisi od odmitnuti serverem. */
export class OfflineError extends Error {
  constructor() {
    super('offline');
    this.name = 'OfflineError';
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl}${PREFIX}${path}`, { credentials: 'include', ...init });
  } catch {
    throw new OfflineError();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { title?: string; detail?: string; type?: string } | null;
    throw new DomainApiError(body?.detail ?? body?.title ?? `HTTP ${String(res.status)}`, res.status, body?.type);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function json(method: string, body: unknown): RequestInit {
  return { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
}

export function getIntakeContext(caseId: string | null): Promise<IntakeContextDto> {
  return req(`/context${caseId === null ? '' : `?caseId=${encodeURIComponent(caseId)}`}`);
}

/**
 * Krok NAB2 — sablona naboroveho checklistu. Prichazi ze serveru jako data
 * (otazky, moznosti, kody Sreality), takze admin nema vlastni kopii bodu.
 *
 * Krok NAB6 — jmeno sablony je parametr: predvolbu pro zakazku i seznam
 * povolenych jmen rika `GET /context` (`checklistKind`,
 * `checklistKindsAvailable`), takze v bundlu neni zadny vlastni seznam typu.
 */
export function getChecklistTemplate(kind: ChecklistTemplateKind = 'byt-prodej'): Promise<ChecklistTemplateDto> {
  return req(`/checklist-template?kind=${encodeURIComponent(kind)}`);
}

/** Krok NAB3 — sablona mistnosti (typy, rozpad dispozice, predvolby) jako data. */
export function getRoomTemplate(): Promise<RoomTemplateDto> {
  return req('/room-template');
}

export function upsertSession(id: string, body: UpsertSessionBody): Promise<IntakeSessionDto> {
  return req(`/sessions/${id}`, json('PUT', body));
}

export function giveConsent(id: string): Promise<IntakeSessionDto> {
  return req(`/sessions/${id}/consent`, json('POST', { ownerName: null }));
}

export function uploadRecording(
  sessionId: string,
  recordingId: string,
  blob: Blob,
  kind: 'conversation' | 'self_dictation',
  durationS: number,
): Promise<unknown> {
  const type = blob.type === '' ? 'audio/webm' : blob.type.split(';')[0] ?? 'audio/webm';
  return req(`/sessions/${sessionId}/recordings/${recordingId}?kind=${kind}&durationS=${String(Math.round(durationS))}`, {
    method: 'POST',
    headers: { 'content-type': type },
    body: blob,
  });
}

/** Krok NAB3: `room` = mistnost, u ktere makler fotil (fotka ji nese do galerie). */
export function uploadPhoto(
  sessionId: string,
  photoId: string,
  blob: Blob,
  atSeconds: number,
  room: { readonly roomId?: string; readonly roomType?: string } = {},
): Promise<unknown> {
  const query = new URLSearchParams({ atSeconds: String(Math.round(atSeconds)) });
  if (room.roomId !== undefined) query.set('roomId', room.roomId);
  if (room.roomType !== undefined) query.set('roomType', room.roomType);
  return req(`/sessions/${sessionId}/photos/${photoId}?${query.toString()}`, {
    method: 'POST',
    headers: { 'content-type': blob.type === '' ? 'image/jpeg' : blob.type },
    body: blob,
  });
}

export function photoUrl(sessionId: string, photoId: string): string {
  return `${baseUrl}${PREFIX}/sessions/${sessionId}/photos/${photoId}`;
}

export function addNote(sessionId: string, noteId: string, atSeconds: number, text: string): Promise<unknown> {
  return req(`/sessions/${sessionId}/notes/${noteId}`, json('PUT', { atSeconds, text }));
}

export function transcribe(sessionId: string): Promise<IntakeSessionDto> {
  return req(`/sessions/${sessionId}/transcribe`, { method: 'POST' });
}

export function analyze(sessionId: string): Promise<{ extractionAvailable: boolean; dropped: number }> {
  return req(`/sessions/${sessionId}/analyze`, { method: 'POST' });
}

/**
 * Krok NAB4 — rozbor DIKTATU ke skupine nebo mistnosti. Nahravka uz je na
 * serveru (poslala ji fronta jako kazdou jinou); tohle rika, co se z ni smi
 * navrhnout, a vraci NAVRHY — nic se nezapisuje.
 */
export function dictate(sessionId: string, body: DictationBody): Promise<DictationResultDto> {
  return req(`/sessions/${sessionId}/dictation`, json('POST', body));
}

export function getSessionView(sessionId: string): Promise<IntakeSessionViewDto> {
  return req(`/sessions/${sessionId}`);
}

export function addManualItem(
  sessionId: string,
  body: { factKey: string; value: unknown; quote: string | null; atSeconds: number | null },
): Promise<IntakeItemDto> {
  return req(`/sessions/${sessionId}/items`, json('POST', body));
}

export function rejectItem(sessionId: string, itemId: string): Promise<void> {
  return req(`/sessions/${sessionId}/items/${itemId}/reject`, { method: 'POST' });
}

export function attachCase(sessionId: string, caseId: string): Promise<IntakeSessionDto> {
  return req(`/sessions/${sessionId}/case`, json('POST', { caseId }));
}

export function confirmItems(sessionId: string, itemIds: readonly string[]): Promise<IntakeConfirmResultDto> {
  return req(`/sessions/${sessionId}/confirm`, json('POST', { itemIds }));
}

export function deleteRecordings(sessionId: string): Promise<void> {
  return req(`/sessions/${sessionId}/recordings`, { method: 'DELETE' });
}
